import { QueryRender } from "@/components/helpers/QueryRender"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import {
   useYoungs,
   useYoungsWithAttendanceForPeriod,
   useYoungsWithAttendanceForRowCall,
} from "@/entities/user/useYoung"
import { useEffect, useMemo, useState } from "react"
import { UserCard } from "@/components/ui/UserCard"
import { format, parseISO, startOfDay, endOfDay, addDays, subDays, isSameDay } from "date-fns"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { IndividualAttendanceMiniDay } from "@/components/attendance/IndividualAttendanceMiniDay"
import { Link } from "react-router-dom"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { getDayQuarters } from "@/entities/globalSettings/globalSettings"
import { computeAttendanceForQuarters } from "@/controller/attendanceCompute"
import { isDevEnv } from "@/config/env"
import { logger } from "@/utils/logger"

export default function DailySummaryPage() {
   const { now } = useTimeMachine()

   const { data: schoolyear, ...query } = useCurrentSchoolYear()

   const [selectedDate, setSelectedDate] = useState(startOfDay(now))

   const timePointOfObservation = useMemo(() => {
      if (isSameDay(now, selectedDate)) {
         // We return the previous minute
         const date = new Date(now)
         date.setSeconds(0, 0)
         return date
      }
      // If future selected date, we return the start of the day
      if (selectedDate > now) return startOfDay(selectedDate)

      // If past selected date, we return the end of past day
      return endOfDay(selectedDate)
   }, [now, selectedDate])

   const { data: globalSettings, isLoading: isGlobalSettingsLoading } = useGlobalSettings()

   const { data: youngs, isLoading: isYoungsLoading } = useYoungs()

   const {
      data: youngsWithAttendanceForTheDay,
      isLoading: isYoungsWithAttendanceForTheDayLoading,
   } = useYoungsWithAttendanceForPeriod({
      schoolyear_id: schoolyear?.id,
      from: startOfDay(selectedDate),
      to: endOfDay(selectedDate),
   })

   const { data: youngsWithAttendanceAtNow, isLoading: isYoungsWithAttendanceAtNowLoading } =
      useYoungsWithAttendanceForRowCall({ now: timePointOfObservation })

   const nowStats = useMemo(() => {
      if (!youngsWithAttendanceAtNow) return null

      const stats = {
         nowPresents: 0,
         nowLates: 0,
      }

      youngsWithAttendanceAtNow.forEach((young) => {
         if (young.current_attendance_status === "present") stats.nowPresents++
         else if (young.current_attendance_status === "late") stats.nowLates++
      })

      return stats
   }, [youngsWithAttendanceAtNow])

   const [futureStats, setFutureStats] = useState<{
      futureComingBack: number
      futureGoingAway: number
   } | null>(null)

   const [_renderKey, setRenderKey] = useState(0)

   const refresh = () => {
      setRenderKey((k) => k + 1)
   }

   const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = parseISO(event.target.value)
      setSelectedDate(newDate)
   }

   const goToPreviousDay = () => {
      setSelectedDate((date) => subDays(date, 1))
   }

   const goToNextDay = () => {
      setSelectedDate((date) => addDays(date, 1))
   }

   useEffect(() => {
      let active = true
      doit()
      return () => {
         active = false
      }

      async function doit() {
         if (!youngs) return
         if (!schoolyear || !globalSettings) return

         setFutureStats(null)

         logger("RECOMPUTING")

         const dayQuarters = getDayQuarters(globalSettings, selectedDate)

         const stats = {
            nowPresents: 0,
            nowLates: 0,
            futureComingBack: 0,
            futureGoingAway: 0,
         }

         const youngsWithQuarters = await Promise.all(
            youngs.map(async (young) => ({
               young,
               quarters: await computeAttendanceForQuarters({
                  youngId: young.id,
                  schoolyearId: schoolyear?.id,
                  day: selectedDate,
                  dayQuarters,
                  globalSettings,
               }),
            })),
         )

         // logger('===> resume-du-jour.tsx:138 ~ youngsWithQuarters', youngsWithQuarters);

         youngsWithQuarters.forEach(({ young, quarters: statusByQuarter }) => {
            let prevStatus: any = null

            dayQuarters.map((quarter, index) => {
               const key = `${selectedDate.toISOString()}-${index}`
               const status = statusByQuarter[key]?.status || "unknown"

               // logger(`[${young.firstName}] Quarter ${index}:`, {
               //    time: quarter.start.toLocaleTimeString(),
               //    status,
               //    prevStatus,
               //    goingAway: (prevStatus === "present" || prevStatus === "late") && status === "absent",
               //    comingBack: prevStatus === "absent" && status === "present",
               //    isFutureQuarter: quarter.start > timePointOfObservation,
               //    quarterStart: quarter.start.toLocaleTimeString(),
               //    timePointOfObservation: timePointOfObservation.toLocaleTimeString()
               // })

               if (prevStatus === null) prevStatus = status
               else {
                  // We want to count status change in the future only.
                  // We want to count when prevStatus changes to nextStatus,
                  // so we need to check that quarter.start is in the future.
                  // Because status changes happens at current quarter.start.
                  const isFutureQuarter = quarter.start > timePointOfObservation
                  if (isFutureQuarter) {
                     if ((prevStatus === "present" || prevStatus === "late") && status === "absent")
                        stats.futureGoingAway++
                     if (prevStatus === "absent" && status === "present") stats.futureComingBack++
                  }

                  prevStatus = status
               }
            })
         })

         if (!active) {
            return
         }
         setFutureStats(stats)
      }
   }, [youngs, schoolyear, globalSettings, timePointOfObservation.toISOString()])

   const absentOrLateYoungs = useMemo(() => {
      if (!youngsWithAttendanceForTheDay) return []

      return youngsWithAttendanceForTheDay
         .filter(
            (young) =>
               young.current_attendance_status === "absent" ||
               young.current_attendance_status === "late" ||
               young.current_attendance_status === "external",
         )
         .sort((a, b) => a.firstName.localeCompare(b.firstName))
   }, [youngsWithAttendanceForTheDay])

   const perfectPresenceYoungs = useMemo(() => {
      if (!youngsWithAttendanceForTheDay) return []

      return youngsWithAttendanceForTheDay
         .filter((young) => young.current_attendance_status === "present")
         .sort((a, b) => a.firstName.localeCompare(b.firstName))
   }, [youngsWithAttendanceForTheDay])

   return (
      <div className="page-section">
         <h1 className="title">Résumé du jour</h1>
         <div className="flex items-center justify-center gap-4 mb-6">
            <button
               className="btn btn-circle btn-outline"
               onClick={goToPreviousDay}
               aria-label="Jour précédent"
            >
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth={2}
                     d="M15 19l-7-7 7-7"
                  />
               </svg>
            </button>
            <div className="flex flex-col items-center gap-1">
               <input
                  type="date"
                  className="input input-bordered"
                  value={format(selectedDate, "yyyy-MM-dd")}
                  onChange={handleDateChange}
               />
            </div>
            <button
               className="btn btn-circle btn-outline"
               onClick={goToNextDay}
               aria-label="Jour suivant"
            >
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth={2}
                     d="M9 5l7 7-7 7"
                  />
               </svg>
            </button>
         </div>

         <QueryRender
            query={query}
            data={futureStats}
            isLoading={
               isGlobalSettingsLoading ||
               isYoungsLoading ||
               isYoungsWithAttendanceForTheDayLoading ||
               isYoungsWithAttendanceAtNowLoading ||
               !futureStats ||
               !nowStats
            }
         >
            {() => (
               <>
                  {(isSameDay(now, selectedDate) || selectedDate >= timePointOfObservation) && (
                     <div className="flex items-center justify-center">
                        <div className="stats stats-vertical sm:stats-horizontal shadow mb-8 mx-auto sscale-75 overflow-x-visible md:scale-100">
                           {isSameDay(now, selectedDate) && (
                              <div className="stat">
                                 <div className="stat-title text-center">
                                    À {format(timePointOfObservation, "HH:mm")}
                                 </div>
                                 <div className="stat-value">
                                    <button
                                       onClick={() => refresh()}
                                       className="btn _btn-ghost _btn-circle btn-sm"
                                    >
                                       Rafraichir
                                    </button>
                                 </div>
                              </div>
                           )}

                           <div className="stat">
                              <div className="stat-title">Présents</div>
                              <div className="stat-value text-success">{nowStats?.nowPresents}</div>
                           </div>
                           <div className="stat">
                              <div className="stat-title">En retard</div>
                              <div className="stat-value text-warning">{nowStats?.nowLates}</div>
                           </div>
                           <div className="stat">
                              <div className="stat-title">Retours dans la journée</div>
                              <div className="stat-value text-success">
                                 {futureStats?.futureComingBack}
                              </div>
                           </div>
                           <div className="stat">
                              <div className="stat-title">Départs dans la journée</div>
                              <div className="stat-value text-error">
                                 {futureStats?.futureGoingAway}
                              </div>
                           </div>
                        </div>
                     </div>
                  )}

                  <div className="divider">Absences ou Retards</div>

                  {/* Absents et retards */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                     {absentOrLateYoungs.map((young) => {
                        return (
                           <Link key={young.id} to={`/admin/resume-du-jeune/${young.id}`}>
                              <UserCard user={young} key={`user-card-${young.id}`}>
                                 {process.env.NODE_ENV === "development" && (
                                    <div className="mt-2 text-xs text-gray-500">
                                       <code>{young.id}</code>
                                    </div>
                                 )}

                                 <IndividualAttendanceMiniDay
                                    youngId={young.id}
                                    day={selectedDate}
                                    schoolyearId={schoolyear?.id ?? ""}
                                 />
                              </UserCard>
                           </Link>
                        )
                     })}
                  </div>

                  <div className="divider">Parfaites présences</div>

                  {/* Parfaitement present */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                     {perfectPresenceYoungs.map((young) => {
                        return (
                           <Link key={young.id} to={`/admin/resume-du-jeune/${young.id}`}>
                              <UserCard user={young} key={`user-card-${young.id}`}>
                                 {process.env.NODE_ENV === "development" && (
                                    <div className="mt-2 text-xs text-gray-500">
                                       <code>{young.id}</code>
                                    </div>
                                 )}

                                 <IndividualAttendanceMiniDay
                                    youngId={young.id}
                                    day={selectedDate}
                                    schoolyearId={schoolyear?.id ?? ""}
                                 />
                              </UserCard>
                           </Link>
                        )
                     })}
                  </div>
               </>
            )}
         </QueryRender>
      </div>
   )
}
